import type { AnyObject } from '../../Types';
import type { ConfigService } from './mx-config-service';
import type { UserProfileService } from './user-profile-service';
import type { HttpService } from './http-service';
import type { I18nService } from '../../i18n/i18n.service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { MxApplicationService } from './mx-application-service';
import type { MxNavigationService } from '../../Navigation/mx-navigation-service';
import type { DataQueryService } from './data-query-service';
import { utils } from '../../Utils/mx-utils';

declare global {
	interface Window {
		Userlane: (fnName: string, param1: string, param2?: AnyObject) => void | AnyObject;
	}
}

export class UserlaneService {
	static readonly $inject = [
		'mx.shell.Config',
		'mx.shell.UserProfileService',
		'mx.shell.Http',
		'mx.shell.InternationalizationService',
		'mx.internationalization',
		'mx.shell.AppsService',
		'mx.shell.NavigationService',
		'mx.SolutionBuilderAgent.DataQueryService',
	];

	constructor(
		private readonly configService: ConfigService,
		private readonly userProfileService: UserProfileService,
		private readonly httpService: HttpService,
		private readonly i18nService: I18nService,
		private readonly i18n: I18n,
		private readonly applicationService: MxApplicationService,
		private readonly navigationService: MxNavigationService,
		private readonly dataQueryService: ReturnType<typeof DataQueryService>
	) {}

	async init() {
		const productToursCfg = this.configService.get('productTours');
		if (productToursCfg) {
			const allowedExtraProps = (productToursCfg.allowedUserData || '').split(',').map((s) => s.trim());
			const profile = await this.userProfileService.get();

			let installedExtensionsResult = '';
			try {
				const dqConfigurationPackageId = '0c058d4a-1087-cdf5-4f01-08d7f1b998bb';
				const installedExtensions = await this.dataQueryService.getDataQueryData(dqConfigurationPackageId);
				installedExtensionsResult = utils.htmlEncode(installedExtensions.map((i: { Name: string }) => i.Name).join(', '));
			} catch (error) {
				console.error('Invalid JSON parsing for installed Extensions:', error);
			}

			setTimeout(async () => {
				this.load();
				const userDataToSend = {
					language: this.i18nService.currentLanguage?.code,
					browser_user_agent: navigator.userAgent,
					browser: this.getUserBrowser(),
					device_os: this.getUserDeviceOS(),
					device_type: this.getUserDeviceType(),
					screen_width: window.screen.width,
					screen_height: window.screen.height,
					screen_pixel_ratio: window.devicePixelRatio,
					country: [navigator.language, this.i18nService.currentLanguage?.code].find((c) => c?.[2] === '-')?.substr(-2) || '',
					UI: document.cookie.includes(`NewUI=1`) ? 'New Look' : 'Classic Look',
					navigation_item: this.navigationService.currentItem?.technicalName || '',
					application: this.applicationService.getCurrentApplication()?.name || '',
					product_version: this.configService.settings.productVersion,
					subscriptions_licenses: (((await this.httpService.get('api/license/availableproducts')) as string[]) || []).join(','),
					user_type: profile.userType,
					user_creation_date: profile.createdDate,
					user_last_activity_date: profile.lastActivityDate,
					installed_extension: installedExtensionsResult,
					...(allowedExtraProps.includes('LastName') && { user_last_name: profile.lastName || '' }),
					...(allowedExtraProps.includes('FirstName') && { user_first_name: profile.firstName || '' }),
					...(allowedExtraProps.includes('Email') && { user_email: profile.email || '' }),
				};
				window.Userlane('identify', profile.id, userDataToSend);
				window.Userlane('init', 'q84ro53m49');

				this.adjustARIA();
			}, 0);
		}
	}

	load() {
		// ORIGIN JS-CODE:
		/*
		(function(i,s,o,g,r,a,m){i['UserlaneCommandObject']=r;i[r]=i[r]||function(){
		(i[r].q=i[r].q||[]).push(arguments)};a=s.createElement(o),
		m=s.getElementsByTagName(o)
		[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m) })
		(window,document,'script','https://cdn.userlane.com/userlane.js','Userlane');
		*/

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(function (i: any, s, o, g, r) {
			let a, m;
			i['UserlaneCommandObject'] = r;
			i[r] =
				i[r] ||
				function () {
					// eslint-disable-next-line prefer-rest-params
					(i[r].q = i[r].q || []).push(arguments);
				};
			(a = s.createElement(o)), (m = s.getElementsByTagName(o)[0] as HTMLElement);
			(a as HTMLScriptElement).async = true;
			(a as HTMLScriptElement).src = g;
			m.parentNode?.insertBefore(a, m);
		})(window, document, 'script', 'https://cdn.userlane.com/userlane.js', 'Userlane');
	}

	getUserDeviceOS() {
		const userAgent = navigator.userAgent;
		if (/iPhone|iPad|iPod/.test(userAgent)) {
			return 'iOS';
		} else if (/Android/.test(userAgent)) {
			return 'Android';
		} else if (/Macintosh/.test(userAgent)) {
			return 'Mac';
		} else if (/Windows/.test(userAgent)) {
			return 'Windows';
		}
		return '';
	}

	getUserBrowser() {
		const userAgent = navigator.userAgent;
		if (/OPR/i.test(userAgent)) {
			return 'Opera';
		} else if (/Edg/i.test(userAgent)) {
			return 'Edge';
		} else if (/Chrome/i.test(userAgent)) {
			return 'Chrome';
		} else if (/Safari/i.test(userAgent)) {
			return 'Safari';
		} else if (/Firefox/i.test(userAgent)) {
			return 'Firefox';
		} else if (/MSIE|Trident/i.test(userAgent)) {
			return 'IE';
		}
		return '';
	}

	getUserDeviceType() {
		const ua = navigator.userAgent;
		const isTouch = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
		// Tablet detection: Android tablets (Android but not Mobile), iPad, common tablets
		if (isTouch) {
			if (/iPad|Tablet|PlayBook|Silk|Nexus 7|Nexus 10|SM-T|GT-P|KFAPWI/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
				return 'tablet';
			}
			// Mobile detection: Android Mobile, iPhone, iPod, Windows Phone
			if (/Mobi|Android.*Mobile|iPhone|iPod|Windows Phone|BlackBerry|webOS/i.test(ua)) {
				return 'mobile';
			}
		}
		return 'desktop';
	}

	adjustARIA() {
		setTimeout(() => {
			const bubbleBtn = $('#usln-p-assistant-avatar');
			if (bubbleBtn.attr('aria-label')?.trim() === 'false') {
				bubbleBtn.attr('aria-label', this.i18n.get('shell.aria.productTours') || 'Product Tours');
			}
		}, 2000);
	}
}
